import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from '../contexts/SnackbarContext';
import { createNotification, deleteNotificationByMetadata } from '../services/notificationService';
import {
  assignTenderSection,
  createTenderSection,
  deleteTenderSection,
  fetchTenderSections,
  lockTenderSection,
  markSectionReviewed,
  resolveTenderSectionSuggestion,
  saveTenderSection,
  unlockTenderSection,
} from '../services/tenderSectionService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Section, SectionUserRef, Tender } from '../types/tender';

export const useTenderSections = (
  tenderId: string,
  account: import('../types/auth').User | null | undefined,
  tenderOrTitle?: Tender | string
) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const { showSnackbar } = useSnackbar();

  const tenderObject = typeof tenderOrTitle === 'object' ? tenderOrTitle : null;
  const tenderTitle = typeof tenderOrTitle === 'string' ? tenderOrTitle : tenderObject?.title;

  const fetchSections = useCallback(async () => {
    try {
      // Determine if user can view all sections or just assigned ones
      // Check Global Permissions
      const hasGlobalPermission =
        account?.cachedPermissions?.includes(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT) ||
        account?.permissions?.includes(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT);

      // Check Tender-Specific Permissions
      const tenderSpecificPermissions = account?.uid
        ? tenderObject?.memberPermissions?.[account.uid]
        : undefined;
      const isOwner = tenderObject?.ownerId ? account?.uid === tenderObject.ownerId : false;

      const canViewAllSections =
        isOwner ||
        (tenderSpecificPermissions !== undefined
          ? tenderSpecificPermissions.includes(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT) ||
            tenderSpecificPermissions.includes(PERMISSIONS.TENDER_FULL_ACCESS)
          : hasGlobalPermission);

      const assignedUserId = canViewAllSections ? undefined : account?.uid;

      const rawSections = await fetchTenderSections(tenderId, assignedUserId);

      const processedSections = await Promise.all(
        rawSections.map(
          async (
            sec: Partial<Section> & {
              encrypted_content?: string;
              encrypted_suggested_content?: string;
            }
          ) => {
            let content = sec.content || '';
            let suggested_content = sec.suggested_content || null;

            if (sec.encrypted_content) {
              content = '[Legacy Encrypted Content - Requires Migration]';
            }
            if (sec.encrypted_suggested_content) {
              suggested_content = '[Legacy Encrypted Content - Requires Migration]';
            }

            if (typeof content === 'string') {
              content = content.replace(/\{\{\s*[^}]+\s*\}\}\s*/g, '');
            }

            if (typeof suggested_content === 'string') {
              suggested_content = suggested_content.replace(/\{\{\s*[^}]+\s*\}\}\s*/g, '');
            }

            return {
              ...sec,
              content,
              suggested_content,
            } as Section;
          }
        )
      );
      setSections(processedSections);
    } catch (error: unknown) {
      const err = error as Error & { code?: string };
      if (err.code === 'permission-denied' || err.message?.includes('permission-denied')) {
        showSnackbar('Permission denied: Unable to load sections.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [
    tenderId,
    account?.uid,
    account?.cachedPermissions,
    account?.permissions,
    showSnackbar,
    tenderObject?.memberPermissions,
    tenderObject?.ownerId,
  ]);

  useEffect(() => {
    fetchSections();
    // Poll for updates (simple for now)
    const interval = setInterval(fetchSections, 5000);
    return () => clearInterval(interval);
  }, [fetchSections]);

  const updateStatus = async (section: Section, newStatus: string) => {
    try {
      const userRef = account
        ? {
            uid: account.uid,
            displayName: account.displayName || account.email || 'Unknown',
            at: new Date(),
          }
        : undefined;
      await markSectionReviewed(
        tenderId,
        section.id,
        newStatus as NonNullable<Section['status']>,
        userRef
      );

      setSections((prev) =>
        prev.map((s) => {
          if (s.id === section.id) {
            const newState = { ...s, status: newStatus as NonNullable<Section['status']> };
            if (newStatus === 'reviewed' || newStatus === 'complete') {
              delete newState.assignedTo;
            }
            if (newStatus !== 'reviewed') {
              delete newState.reviewedBy;
            }
            return newState;
          }
          return s;
        })
      );

      // Handle notification removal when marked as reviewed or complete
      if ((newStatus === 'reviewed' || newStatus === 'complete') && section.assignedTo) {
        // Remove notification for the assigned user
        await deleteNotificationByMetadata(section.assignedTo.uid, {
          tenderId,
          sectionId: section.id,
          type: 'assignment_action',
        });
      }
    } catch (_e) {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const saveContent = async (section: Section, newContent: string) => {
    try {
      const userRef = account
        ? {
            uid: account.uid,
            displayName: account.displayName || account.email || 'Unknown',
            at: new Date(),
          }
        : undefined;
      await saveTenderSection(
        tenderId,
        section.id,
        newContent,
        section.title,
        section.index,
        'in_progress',
        section.level,
        userRef
      );
      setSections((prev) =>
        prev.map((s) =>
          s.id === section.id
            ? {
                ...s,
                content: newContent,
                status: 'in_progress',
                ...(userRef ? { lastEditedBy: userRef } : {}),
              }
            : s
        )
      );
      showSnackbar('Section saved', 'success');
      return true;
    } catch (_e) {
      showSnackbar('Failed to save section', 'error');
      return false;
    }
  };

  const lock = async (section: Section) => {
    if (!account) {
      return;
    }
    try {
      const userRef = {
        uid: account.uid,
        displayName: account.displayName || account.email || 'Unknown',
        at: new Date(),
      };
      await lockTenderSection(tenderId, section.id, userRef);
      setSections((prev) =>
        prev.map((s) => (s.id === section.id ? { ...s, lockedBy: userRef } : s))
      );
    } catch (_e) {
      showSnackbar('Failed to lock section', 'error');
    }
  };

  const unlock = async (section: Section) => {
    try {
      await unlockTenderSection(tenderId, section.id);
      setSections((prev) =>
        prev.map((s) => {
          if (s.id === section.id) {
            const { lockedBy, ...rest } = s;
            return rest;
          }
          return s;
        })
      );
    } catch (_e) {
      showSnackbar('Failed to unlock section', 'error');
    }
  };

  const assign = async (
    section: Section,
    user: Record<string, unknown> | null,
    permissions?: string[]
  ) => {
    try {
      const userRef = user
        ? { uid: user.uid, displayName: user.displayName || user.email, email: user.email }
        : null;

      const assignerRef = account
        ? {
            uid: account.uid,
            displayName: account.displayName || account.email || 'Unknown',
            email: account.email,
          }
        : null;

      await assignTenderSection(
        tenderId,
        section.id,
        userRef as SectionUserRef | null,
        assignerRef as SectionUserRef | null,
        permissions
      );
      setSections((prev) =>
        prev.map((s) => {
          if (s.id === section.id) {
            const newState = { ...s };
            if (userRef) {
              newState.assignedTo = userRef as SectionUserRef;
              newState.status = 'in_progress';
              delete newState.reviewedBy;
            } else {
              delete newState.assignedTo;
            }
            return newState;
          }
          return s;
        })
      );

      if (user) {
        // Create Action Notification
        const ownerName = account?.displayName || account?.email || 'The Tender Owner';
        await createNotification({
          userId: user.uid as string,
          title: 'Action Required: Section Assigned',
          message: `${ownerName} has assigned you to review the section "${section.title}" in tender "${tenderTitle || 'Untitled Tender'}".`,
          link: `/tender/${tenderId}?step=7&actionSectionId=${section.id}`,
          dismissible: false,
          type: 'action',
          metadata: {
            tenderId,
            sectionId: section.id,
            type: 'assignment_action',
          },
        });
      } else if (section.assignedTo) {
        // If unassigned, remove the notification
        await deleteNotificationByMetadata(section.assignedTo.uid, {
          tenderId,
          sectionId: section.id,
          type: 'assignment_action',
        });
      }

      showSnackbar(user ? 'Assigned' : 'Unassigned', 'success');
    } catch (_e) {
      showSnackbar('Failed to assign', 'error');
    }
  };

  const resolveSuggestion = async (section: Section, accept: boolean, overrideContent?: string) => {
    try {
      if (accept) {
        const contentToUse = overrideContent ?? section.suggested_content;
        if (contentToUse) {
          await resolveTenderSectionSuggestion(tenderId, section.id, contentToUse);
          setSections((prev) =>
            prev.map((s) => {
              if (s.id === section.id) {
                return {
                  ...s,
                  content: contentToUse,
                  suggested_content: undefined,
                  cross_boundary_suggestion: undefined,
                  suggested_by: undefined,
                  status: 'in_progress',
                };
              }
              return s;
            })
          );
          showSnackbar('Suggestion accepted', 'success');
        }
      } else {
        await resolveTenderSectionSuggestion(tenderId, section.id, null);
        setSections((prev) =>
          prev.map((s) => {
            if (s.id === section.id) {
              return {
                ...s,
                suggested_content: undefined,
                cross_boundary_suggestion: undefined,
                suggested_by: undefined,
              };
            }
            return s;
          })
        );
        showSnackbar('Suggestion rejected', 'info');
      }
    } catch (_e) {
      showSnackbar(accept ? 'Failed to accept suggestion' : 'Failed to reject suggestion', 'error');
    }
  };

  const updateSectionState = (sectionId: string, updates: Partial<Section>) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)));
  };

  const createSection = async (title: string, level: number, index: number) => {
    try {
      const id = await createTenderSection(tenderId, title, level, index);
      const newSection: Section = {
        id,
        title,
        content: '',
        index,
        status: 'pending',
        level,
      };
      setSections((prev) => [...prev, newSection].sort((a, b) => a.index - b.index));
      showSnackbar('Section created', 'success');
      return id;
    } catch (_e) {
      showSnackbar('Failed to create section', 'error');
      return null;
    }
  };

  const deleteSection = async (sectionId: string) => {
    try {
      await deleteTenderSection(tenderId, sectionId);
      setSections((prev) => prev.filter((s) => s.id !== sectionId));
      showSnackbar('Section deleted', 'success');
    } catch (_e) {
      showSnackbar('Failed to delete section', 'error');
    }
  };

  const reorderSections = (newSections: Section[]) => {
    setSections(newSections);
  };

  return {
    sections,
    loading,
    updateStatus,
    saveContent,
    resolveSuggestion,
    refresh: fetchSections,
    updateSectionState,
    createSection,
    deleteSection,
    reorderSections,
    lock,
    unlock,
    assign,
  };
};
